import React, { useState } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import {
  CheckCircle, Truck, MapPin, Phone, Camera, Mic,
  Clock, User, Heart, AlertTriangle, Send, X
} from 'lucide-react';
import LiveCameraFeeds from './LiveCameraFeeds';

export default function AlertDetail({ alert, timeline, dispatches, locations, teams, onStatusUpdate, onDispatch }) {
  const [showDispatch, setShowDispatch] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [eta, setEta] = useState(10);

  if (!alert) return null;

  const handleDispatch = () => {
    if (!selectedTeam) return;
    onDispatch(alert.id, selectedTeam, [], eta);
    setShowDispatch(false);
    setSelectedTeam('');
  };

  const statusActions = {
    pending: [
      { status: 'acknowledged', label: 'Acknowledge', icon: CheckCircle, cls: 'btn-primary' },
    ],
    acknowledged: [
      { status: 'dispatched', label: 'Dispatch Team', icon: Truck, cls: 'btn-primary', action: () => setShowDispatch(true) },
    ],
    dispatched: [
      { status: 'en_route', label: 'Mark En Route', icon: Truck, cls: 'btn' },
    ],
    en_route: [
      { status: 'on_scene', label: 'On Scene', icon: MapPin, cls: 'btn-success' },
    ],
    on_scene: [
      { status: 'resolved', label: 'Resolve', icon: CheckCircle, cls: 'btn-success' },
    ],
  };

  const actions = statusActions[alert.status] || [];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{
        padding: '16px 18px', borderBottom: '1px solid var(--border)',
        background: alert.category_color ? `${alert.category_color}10` : 'transparent'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 24 }}>{alert.category_icon}</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{alert.category_name}</div>
            <span className={`status-badge status-${alert.status}`} style={{ marginTop: 2 }}>
              {alert.status.replace('_', ' ')}
            </span>
          </div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-display)' }}>
          ID: {alert.id?.substring(0, 8)}... • {alert.trigger_method?.toUpperCase()} TRIGGER
        </div>
      </div>

      {/* Content scrollable */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0' }}>

        {/* User Info */}
        <div className="panel-body" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="panel-title" style={{ marginBottom: 10 }}>
            <User size={12} style={{ marginRight: 4 }} /> PERSON IN DISTRESS
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
            <div><strong>{alert.user_name}</strong></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--text-secondary)' }}>
              <Phone size={12} /> {alert.user_phone}
            </div>
            {alert.blood_type && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--accent-red)' }}>
                <Heart size={12} /> Blood Type: {alert.blood_type}
              </div>
            )}
            {alert.medical_notes && (
              <div style={{ fontSize: 11, color: 'var(--accent-amber)', background: 'rgba(245,158,11,0.1)', padding: '6px 10px', borderRadius: 6, marginTop: 4 }}>
                <AlertTriangle size={11} /> {alert.medical_notes}
              </div>
            )}
          </div>
        </div>

        {/* Location */}
        <div className="panel-body" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="panel-title" style={{ marginBottom: 8 }}>
            <MapPin size={12} style={{ marginRight: 4 }} /> LOCATION
          </div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 12, color: 'var(--text-secondary)' }}>
            {Number(alert.latitude).toFixed(6)}, {Number(alert.longitude).toFixed(6)}
          </div>
          {alert.address && <div style={{ fontSize: 12, marginTop: 4 }}>{alert.address}</div>}
        </div>

        {/* Dual Camera Live Feeds */}
        <div style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="panel-body" style={{ paddingBottom: 8 }}>
            <div className="panel-title">
              <Camera size={12} style={{ marginRight: 4 }} /> LIVE FEEDS
            </div>
          </div>
          <LiveCameraFeeds alertId={alert.id} />
        </div>

        {/* Dispatch Panel */}
        {showDispatch && (
          <div className="panel-body" style={{ borderBottom: '1px solid var(--border)', background: 'rgba(59, 130, 246, 0.05)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div className="panel-title">DISPATCH TEAM</div>
              <button onClick={() => setShowDispatch(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                <X size={14} />
              </button>
            </div>
            <div style={{ marginBottom: 10 }}>
              <label className="form-label">Select Team</label>
              <select className="form-input" value={selectedTeam} onChange={e => setSelectedTeam(e.target.value)}>
                <option value="">-- Choose team --</option>
                {teams.map(t => (
                  <option key={t.id} value={t.id}>{t.category_icon} {t.name} ({t.available_count} available)</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 10 }}>
              <label className="form-label">ETA (minutes)</label>
              <input type="number" className="form-input" value={eta} onChange={e => setEta(parseInt(e.target.value) || 0)} min={1} max={120} />
            </div>
            <button className="btn btn-primary" onClick={handleDispatch} style={{ width: '100%', justifyContent: 'center' }}>
              <Send size={14} /> Dispatch Now
            </button>
          </div>
        )}

        {/* Active Dispatches */}
        {dispatches?.length > 0 && (
          <div className="panel-body" style={{ borderBottom: '1px solid var(--border)' }}>
            <div className="panel-title" style={{ marginBottom: 10 }}>
              <Truck size={12} style={{ marginRight: 4 }} /> DISPATCHED TEAMS
            </div>
            {dispatches.map(d => (
              <div key={d.id} style={{
                background: 'var(--bg-card)', padding: '10px 12px', borderRadius: 'var(--radius-sm)',
                marginBottom: 6, fontSize: 12, border: '1px solid var(--border)'
              }}>
                <div style={{ fontWeight: 600 }}>{d.team_name}</div>
                <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>
                  Dispatched by {d.dispatched_by_name} • ETA: {d.eta_minutes || '?'} min
                </div>
                <span className={`status-badge status-${d.status}`} style={{ marginTop: 6 }}>
                  {d.status}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Timeline */}
        {timeline?.length > 0 && (
          <div className="panel-body">
            <div className="panel-title" style={{ marginBottom: 10 }}>
              <Clock size={12} style={{ marginRight: 4 }} /> TIMELINE
            </div>
            {timeline.map((entry, i) => (
              <div key={entry.id} style={{
                display: 'flex', gap: 10, fontSize: 11, marginBottom: 10,
                paddingLeft: 10, borderLeft: '2px solid var(--border)',
                color: 'var(--text-secondary)'
              }}>
                <div style={{ minWidth: 60, fontFamily: 'var(--font-display)', color: 'var(--text-muted)' }}>
                  {entry.created_at ? format(new Date(entry.created_at), 'HH:mm:ss') : ''}
                </div>
                <div>{entry.action.replace(/_/g, ' ').toUpperCase()}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {actions.length > 0 && (
        <div style={{
          padding: '12px 16px', borderTop: '1px solid var(--border)',
          display: 'flex', gap: 8, background: 'var(--bg-secondary)'
        }}>
          {actions.map(action => (
            <button
              key={action.status}
              className={`btn ${action.cls}`}
              style={{ flex: 1, justifyContent: 'center' }}
              onClick={() => action.action ? action.action() : onStatusUpdate(alert.id, action.status)}
            >
              <action.icon size={14} /> {action.label}
            </button>
          ))}
          {alert.status !== 'resolved' && alert.status !== 'cancelled' && (
            <button
              className="btn btn-danger"
              onClick={() => onStatusUpdate(alert.id, 'cancelled')}
              style={{ fontSize: 11 }}
            >
              <X size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
